/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useRef, useState} from 'react'
import { useReportSearchMutation, useReportLoadMoreMutation, useReportGetByIdMutation } from '../Report.api'
import { useAppSelector, useAppDispatch } from '../../store/Store'
import {
  selectReports,
  selectReport,
  selectReportLoading,
  selectReportsPagination,
  selectReportTimeRangeSelection,
  selectReportFiltersSelection,
  setReportTimeRangeSelection,
  setReportFiltersSelection,
  SerializableTimeRange,
} from '../Report.slice'
import { SearchList } from '../../../common/component/SearchList'
import {ReportListItem} from '../components/ReportListItem'
import { ReportPreview } from '../components/ReportPreview'
import { IReportFilter } from '../Report.model'
import { IFilterDto } from '../../../common/model/Core.model'
import { TagInputSearchControls } from '../../../common/component/TagInputSearchControls'
import { MessageToast, MessageType } from '../../../common/component/MessageToast'
import { Toast } from 'bootstrap'
import { useIntl } from 'react-intl'
import { PageLink, PageTitle } from '../../../../_metronic/layout/core/PageData'
import { TimeRange } from '../../../common/component/DateRangeSelector'
import { getEntry, removeEntry, setEntry } from '../../../service/LocalStorage.service'
import { recalibrateTimeRange } from '../../../util/TimeRange.util'

const REPORT_TIME_RANGE_LS_KEY = 'report_timeRangeSelection'
const REPORT_FILTERS_LS_KEY = 'report_filtersSelection'

const ReportDashboard: FC = () => {
  const intl = useIntl()
  const breadCrumbs: Array<PageLink> = [
    {
      title: intl.formatMessage({id: 'BREADCRUMBS.REPORTS'}),
      path: '/reports',
      isSeparator: false,
      isActive: false,
    },
  ]

  const didFetchInitialData = useRef(false)
  const didFetchNextPage = useRef(false)
  const didFetchSearch = useRef(false)
  const [toastEntry, setToastEntry] = useState<[MessageType, string]>()
  const filterKeys = {
    defaultKey: IReportFilter.CLIENT,
    defaultKeyList: [
      IReportFilter.ID,
      IReportFilter.CLIENT,
      IReportFilter.STATUS,
      // IReportFilter.GUI,
      IReportFilter.FAMILY,
      IReportFilter.AREA,
      IReportFilter.BRAND,
      IReportFilter.MODEL,
      IReportFilter.SERIAL,
      IReportFilter.ADDRESS,
      IReportFilter.SHARED,
    ],
  }
  const [isSearchLoading, setIsSearchLoading] = useState(false)
  const [isSearchControlEnabled, setIsSearchControlEnabled] = useState(false)
  const [filterSuggestions, setFilterSuggestions] = useState<string[]>([])
  const [rowSelected, setRowSelected] = useState<string | undefined>(undefined)

  const dispatch = useAppDispatch()
  const timeRangeSelection = useAppSelector(selectReportTimeRangeSelection)
  const filters = useAppSelector(selectReportFiltersSelection)

  const sanitizePersistedFilters = (candidateFilters: unknown): IFilterDto[] => {
    if (!Array.isArray(candidateFilters)) {
      return []
    }
    return candidateFilters.filter((item): item is IFilterDto => {
      if (!item || typeof item !== 'object') {
        return false
      }
      const filter = item as IFilterDto
      return (
        typeof filter.id === 'string' &&
        filterKeys.defaultKeyList.includes(filter.id as IReportFilter) &&
        typeof filter.value === 'string' &&
        typeof filter.editable === 'boolean'
      )
    })
  }

  const persistFiltersSelection = (nextFilters: IFilterDto[]) => {
    dispatch(setReportFiltersSelection(nextFilters))
    if (nextFilters.length > 0) {
      setEntry(REPORT_FILTERS_LS_KEY, JSON.stringify(nextFilters))
    } else {
      removeEntry(REPORT_FILTERS_LS_KEY)
    }
  }

  const setFilters = (nextFilters: IFilterDto[]) => {
    persistFiltersSelection(nextFilters)
  }

  const handleTimeRangeChange = (range: TimeRange) => {
    const serializable: SerializableTimeRange = {
      timeRangeItem: range.timeRangeItem,
      from: range.from.getTime(),
      to: range.to.getTime(),
    }
    dispatch(setReportTimeRangeSelection(serializable))
    setEntry(REPORT_TIME_RANGE_LS_KEY, JSON.stringify(serializable))
  }

  const [assetReportSearch] = useReportSearchMutation()
  const [assetReportLoadMore] = useReportLoadMoreMutation()
  const [assetReportGetById] = useReportGetByIdMutation()

  const reports = useAppSelector(selectReports)
  const reportsPagination = useAppSelector(selectReportsPagination)
  const report = useAppSelector(selectReport)
  const reportLoading = useAppSelector(selectReportLoading)

  useEffect(() => {
    if (toastEntry) {
      const [messageType] = toastEntry
      const toastElement = document.getElementById('#CustomToast')
      const delay =
        messageType === MessageType.ERROR || messageType === MessageType.WARNING ? 10000 : 2000
      if (toastElement) {
        toastElement.addEventListener('hidden.bs.toast', () => setToastEntry(undefined))
        const toast = new Toast(toastElement, {autohide: true, delay})
        toast.show()
      }
    }
  }, [toastEntry])

  useEffect(() => {
    let initialRange: TimeRange | undefined
    let initialFilters: IFilterDto[] | undefined

    const savedFilters = getEntry(REPORT_FILTERS_LS_KEY)
    if (savedFilters) {
      try {
        const parsedFilters = JSON.parse(savedFilters)
        const sanitizedFilters = sanitizePersistedFilters(parsedFilters)
        persistFiltersSelection(sanitizedFilters)
        initialFilters = sanitizedFilters
      } catch {
        persistFiltersSelection([])
      }
    }

    const saved = getEntry(REPORT_TIME_RANGE_LS_KEY)
    if (saved) {
      try {
        const parsed: SerializableTimeRange = JSON.parse(saved)
        const recalibratedRange = recalibrateTimeRange(parsed)
        const recalibratedSerializable: SerializableTimeRange = {
          timeRangeItem: recalibratedRange.timeRangeItem,
          from: recalibratedRange.from.getTime(),
          to: recalibratedRange.to.getTime(),
        }
        dispatch(setReportTimeRangeSelection(recalibratedSerializable))
        setEntry(REPORT_TIME_RANGE_LS_KEY, JSON.stringify(recalibratedSerializable))
        initialRange = recalibratedRange
      } catch {}
    }
    if (initialFilters && initialFilters.length > 0) {
      const filterIds = initialFilters.map((filter) => filter.id)
      setFilterSuggestions(
        filterKeys.defaultKeyList.filter((defaultKey) => !filterIds.includes(defaultKey))
      )
    } else {
      setFilterSuggestions(filterKeys.defaultKeyList)
    }

    fetchInitialData(initialRange, initialFilters)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchInitialData = async (rangeOverride?: TimeRange, filtersOverride?: IFilterDto[]) => {
    const range = rangeOverride ?? timeRangeSelection
    const requestFilters = filtersOverride ?? filters
    try {
      setIsSearchLoading(true)
      if (!didFetchInitialData.current) {
        const request: any = {
          timeRange: {
            ...range,
          },
        }
        if (requestFilters.length > 0) {
          request.filters = requestFilters
        }
        const response: any = await assetReportSearch(request)
        if (response.data) {
          setIsSearchControlEnabled(true)
          if (response.data.filters) {
            const filterIds = response.data.filters.map((filter) => filter.id)
            persistFiltersSelection(response.data.filters)
            setFilterSuggestions(
              filterKeys.defaultKeyList.filter((defaultKey) => !filterIds.includes(defaultKey))
            )
          } else {
            const filterIds = requestFilters.map((filter) => filter.id)
            setFilterSuggestions(
              filterKeys.defaultKeyList.filter((defaultKey) => !filterIds.includes(defaultKey))
            )
          }
        }
      }
    } finally {
      setIsSearchLoading(false)
    }
  }

  const nextPageOnClickHandler = async () => {
    try {
      setIsSearchLoading(true)
      if (!didFetchNextPage.current) {
        await assetReportLoadMore({
          pagination: {
            limit: reportsPagination?.limit,
            sort: reportsPagination?.sort,
            page: reportsPagination?.page,
          },
          filters: filters,
          timeRange: {
            ...timeRangeSelection,
          },
        })
      }
    } finally {
      setIsSearchLoading(false)
    }
  }

  const searchOnClickHandler = async () => {
    try {
      setIsSearchLoading(true)
      if (!didFetchSearch.current) {
        await assetReportSearch({
          pagination: {
            sort: reportsPagination?.sort,
          },
          filters: filters,
          timeRange: {
            ...timeRangeSelection,
          },
        })
      }
    } finally {
      setIsSearchLoading(false)
    }
  }

  const resetSearchOnClickHandler = async () => {
    persistFiltersSelection([])
    setFilterSuggestions(filterKeys.defaultKeyList)
    await fetchInitialData(undefined, [])
  }

  const rowOnClickHandler = async (id: string) => {
    setRowSelected(id)
    if (!report || report.id !== id) {
      await assetReportGetById({id, fields: 'LIMITED'})
    }
  }

  return (
    <>
      {toastEntry && <MessageToast messageType={toastEntry[0]} message={toastEntry[1]} />}
      <PageTitle breadcrumbs={breadCrumbs}></PageTitle>
      <div className='flex mt-10'>
        <div className='row g-5'>
          <SearchList
            loading={isSearchLoading}
            rowSelected={rowSelected}
            nextPage={reportsPagination?.page}
            nextPageOnClickHandler={nextPageOnClickHandler}
            searchControlsComponent={
              <TagInputSearchControls
                loading={isSearchLoading}
                enabled={isSearchControlEnabled}
                filterKeys={filterKeys}
                filterSuggestions={filterSuggestions}
                filters={filters}
                setFilterSuggestions={setFilterSuggestions}
                setFilters={setFilters}
                createOnClickPath='/reports/new'
                createOnClickOpenNew={false}
                searchOnClickHandler={searchOnClickHandler}
                resetSearchResetOnClickHandler={resetSearchOnClickHandler}
                timeRangeSelection={timeRangeSelection}
                timeRangeOnClickHandler={handleTimeRangeChange}
              ></TagInputSearchControls>
            }
            listComponent={reports.map((item, index) => (
              <div key={index}>
                <ReportListItem
                  data={item}
                  rowSelected={rowSelected}
                  onRowClick={rowOnClickHandler}
                ></ReportListItem>
                {index !== 0 && <div className='separator'></div>}
              </div>
            ))}
          ></SearchList>

          {rowSelected && (
            <ReportPreview
              report={report}
              loading={reportLoading}
              setRowSelected={setRowSelected}
              setToastEntry={setToastEntry}
            ></ReportPreview>
          )}
        </div>
      </div>
    </>
  )
}

export {ReportDashboard}
